import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from "../config";
import { toast } from 'react-toastify';
import {
  FaFileInvoice, FaPlus, FaEye, FaDownload,
  FaEnvelope, FaTrash, FaFilter, FaSearch,
  FaCheck, FaClock, FaTimes, FaPrint,
  FaPaperclip, FaMoneyBillWave, FaListUl, FaSpinner, FaUndo
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ClientInvoicePDFButton from '../components/ClientInvoicePDFButton';
import ProformaPDFButton from '../components/ProformaPDFButton';
import CreditNotePDFButton from '../components/CreditNotePDFButton';
import ConfirmDialog from '../components/ConfirmDialog';
import ReceivePaymentButton from '../components/ReceivePaymentButton';
import { invoiceBalance } from '../utils/invoiceBalance';

// Selectable payment terms on the Create Invoice form (auto-defaults to the
// client's own terms but stays editable). Kept in the same order the user
// expects; legacy strings are normalised by defaultTerm().
const PAYMENT_TERM_OPTIONS = [
  'Due on Receipt',
  '7 Days',
  '14 Days',
  '30 Days',
  'COD',
  '50% Booking/50% EPOD',
  '50% Booking/50% Completion'
];

const TERM_SYNONYMS = {
  'due on receipt': 'Due on Receipt',
  'pay in advance': 'Due on Receipt',
  'pay in advance (prepaid)': 'Due on Receipt',
  'prepaid': 'Due on Receipt',
  'cod': 'COD',
  '7 days': '7 Days',
  '14 days': '14 Days',
  '15 days': '15 Days',
  '30 days': '30 Days',
  '50% booking/50% epod': '50% Booking/50% EPOD',
  '50% booking/50% completion': '50% Booking/50% Completion'
};
const defaultTerm = (raw) => TERM_SYNONYMS[String(raw || '').trim().toLowerCase()] || '30 Days';

function InvoicesPage({ onLogout }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('30 Days');
  const [vatIncluded, setVatIncluded] = useState(true);
  const [isSupplementary, setIsSupplementary] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState([{ description: '', amount: '', cost_price: '' }]);

  // Subbie cost allocation on the invoice detail
  const [subbies, setSubbies] = useState([]);
  const [invoicePayables, setInvoicePayables] = useState([]);
  const [allocForm, setAllocForm] = useState({ subcontractor_id: '', amount: '' });
  const [allocating, setAllocating] = useState(false);

  // Issue invoice (proforma -> tax invoice)
  const [issueTarget, setIssueTarget] = useState(null);
  const [issueTerms, setIssueTerms] = useState('30 Days');
  const [issuing, setIssuing] = useState(false);

  // Record payment
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: '', method: 'Bank Transfer', reference: '', notes: '', pop_file: null });
  const [savingPay, setSavingPay] = useState(false);

  // Client credit available to allocate on the invoice being issued / paid
  // (credit_notes that are still open for this client).
  const [creditInfo, setCreditInfo] = useState(null);    // { total, notes: [{id, number, available}] }
  const [creditTotals, setCreditTotals] = useState(null); // fresh totals for the target invoice
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [useCredit, setUseCredit] = useState(false);
  const [creditNoteId, setCreditNoteId] = useState('');
  const [creditAmount, setCreditAmount] = useState('');

  // Credit invoice (full credit + rewind the job chain)
  const [creditTarget, setCreditTarget] = useState(null);
  const [creditReason, setCreditReason] = useState('');
  const [crediting, setCrediting] = useState(false);

  // Credit notes list (view + download)
  const [showCreditNotes, setShowCreditNotes] = useState(false);
  const [creditNotes, setCreditNotes] = useState([]);
  const [loadingCreditNotes, setLoadingCreditNotes] = useState(false);

  // Statement
  const [statement, setStatement] = useState(null);
  const [statementTarget, setStatementTarget] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);

  // PO upload
  const [poTarget, setPoTarget] = useState(null);
  const [poForm, setPoForm] = useState({ ref_number: '', po_file: null });
  const [uploadingPo, setUploadingPo] = useState(false);

  // Detail view
  const [detail, setDetail] = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/invoices`);
      const data = await response.json();
      if (data.success) {
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchJobs();
    fetchSubbies();
  }, [fetchInvoices]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/clients`);
      const data = await response.json();
      if (data.success) {
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API_URL}/jobs`);
      const data = await response.json();
      if (data.success) {
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs for invoice form:', error);
    }
  };

  const fetchSubbies = async () => {
    try {
      const response = await fetch(`${API_URL}/subcontractors`);
      const data = await response.json();
      const list = data?.subcontractors || data?.data || data?.subbies || (Array.isArray(data) ? data : []);
      setSubbies(list);
    } catch (error) {
      console.error('Error fetching subcontractors:', error);
    }
  };

  const fetchSubbieCosts = async (invoiceId) => {
    try {
      const response = await fetch(`${API_URL}/invoices/${invoiceId}/subbie-costs`);
      const data = await response.json();
      if (data.success) setInvoicePayables(data.payables || []);
    } catch (error) {
      console.error('Error fetching subbie costs:', error);
    }
  };

  const submitSubbieAlloc = async () => {
    if (!allocForm.subcontractor_id || !parseFloat(allocForm.amount)) {
      toast.error('Pick a subcontractor and enter the cost amount');
      return;
    }
    setAllocating(true);
    try {
      const response = await fetch(`${API_URL}/invoices/${viewTarget.id}/subbie-cost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subcontractor_id: allocForm.subcontractor_id,
          amount: parseFloat(allocForm.amount)
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Subbie cost allocated (${data.payable?.invoice_number}) — it now appears under Subbie Payments`);
        setAllocForm({ subcontractor_id: '', amount: '' });
        fetchSubbieCosts(viewTarget.id);
      } else {
        toast.error(data.error || 'Failed to allocate subbie cost');
      }
    } catch (error) {
      console.error('Error allocating subbie cost:', error);
      toast.error('Failed to allocate subbie cost: ' + (error.message || error));
    } finally {
      setAllocating(false);
    }
  };

  // Client picked manually → clear any linked job and default terms to the
  // client's own payment terms (still editable below).
  const handleClientChange = (id) => {
    setSelectedClient(id);
    setSelectedJob('');
    setIsSupplementary(false);
    const c = clients.find(x => x.id === id);
    setPaymentTerms(defaultTerm(c?.payment_terms));
  };

  // Job picked → auto-fill client + terms from that job's client. Jobs that
  // already carry an invoice remain selectable: ticking "Supplementary charge"
  // lets you add an extra invoice ON TOP of the existing one (same job).
  const handleJobChange = (id) => {
    setSelectedJob(id);
    setIsSupplementary(false);
    if (!id) return;
    const job = jobs.find(x => x.id === id);
    if (!job) return;
    if (job.client_id) {
      setSelectedClient(job.client_id);
      const c = clients.find(x => x.id === job.client_id);
      setPaymentTerms(defaultTerm(c?.payment_terms));
    }
  };

  const getDueHint = () => {
    if (paymentTerms === 'Due on Receipt') return '→ Due immediately on the invoice date';
    if (paymentTerms === 'COD') return '→ Due on delivery';
    if (paymentTerms === '50% Booking/50% EPOD') return '→ 50% on booking, 50% on ePOD';
    if (paymentTerms === '50% Booking/50% Completion') return '→ 50% on booking, 50% on completion';
    const n = parseInt(paymentTerms, 10);
    return n ? `→ Due ${n} days from the invoice date` : '→ Due 30 days from the invoice date';
  };

  // Live totals for the create-form preview (mirrors the quote page).
  const formSubtotal = invoiceItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const formVat = vatIncluded ? formSubtotal * 0.15 : 0;
  const formTotal = formSubtotal + formVat;

  // Existing (non-cancelled) invoice per job → used to flag already-invoiced
  // jobs in the Job dropdown (one standard invoice per job is enforced in the DB).
  const jobInvoiceMap = {};
  invoices.forEach(inv => {
    if (inv.job_id && inv.status !== 'cancelled' && !jobInvoiceMap[inv.job_id]) {
      jobInvoiceMap[inv.job_id] = { num: inv.invoice_number || inv.proforma_number || 'invoice', id: inv.id };
    }
  });

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    const items = invoiceItems.filter(item => item.description && item.amount);
    if (items.length === 0) {
      toast('Please add at least one line item');
      return;
    }

    const jobHasInvoice = !!(selectedJob && jobInvoiceMap[selectedJob]);
    if (jobHasInvoice && !isSupplementary) {
      toast.error(`This job already has ${jobInvoiceMap[selectedJob].num}. Tick “Supplementary charge” to add an extra invoice on top of it, or remove the job link.`);
      return;
    }
    const invoiceType = jobHasInvoice ? 'supplementary' : 'standard';

    const invoiceData = {
      client_id: selectedClient,
      job_id: selectedJob || null,
      invoice_type: invoiceType,
      payment_terms: paymentTerms,
      items: items.map(item => ({
        description: item.description,
        amount: parseFloat(item.amount),
        cost_price: parseFloat(item.cost_price) || 0,
        margin_percentage: item.cost_price ? Math.round(((parseFloat(item.amount) - parseFloat(item.cost_price)) / parseFloat(item.amount)) * 1000) / 10 : 0
      })),
      invoice_date: new Date().toISOString().split('T')[0],
      vat_included: vatIncluded
    };

    try {
      const response = await fetch(`${API_URL}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceData)
      });
      let data = {};
      try {
        data = await response.json();
      } catch (parseErr) {
        data = { error: `Server returned an invalid response (HTTP ${response.status})` };
      }
      if (!response.ok || !data.success) {
        console.error('Create invoice failed:', response.status, data);
        toast.error(data.error || data.message || `Create invoice failed (HTTP ${response.status})`);
        return;
      }
      setShowCreateModal(false);
      fetchInvoices();
      toast.success(`Invoice ${data.invoice?.invoice_number || ''} created successfully!`);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice: ' + (error.message || error));
    }
  };

  const [viewTarget, setViewTarget] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');


  // ============================================
  // CLIENT CREDIT ON THIS INVOICE
  // ============================================
  // Fetches the client's open credit for the invoice target and pre-fills the
  // "allocate this credit?" prompt. Used by BOTH the Issue and Payment modals.
  const loadAvailableCredit = async (invoice) => {
    if (!invoice?.id) return;
    setLoadingCredit(true);
    setCreditInfo(null);
    setCreditTotals(null);
    setUseCredit(false);
    setCreditNoteId('');
    setCreditAmount('');
    try {
      const res = await fetch(`${API_URL}/invoices/${invoice.id}/available-credit`);
      const data = await res.json();
      if (data.success) {
        setCreditInfo(data.available_credit || null);
        setCreditTotals(data.totals || null);
        if (data.can_allocate && Number(data.suggest_amount) > 0) {
          const notes = data.available_credit?.notes || [];
          setUseCredit(true);
          setCreditNoteId(notes[0]?.id || '');
          setCreditAmount(String(data.suggest_amount));
        }
      }
    } catch (error) {
      console.error('Error loading client credit:', error);
    } finally {
      setLoadingCredit(false);
    }
  };

  // Allocates the selected credit amount (when the admin ticked the box).
  // Returns { applied, message?, error? } — never throws.
  const applyCreditIfNeeded = async (invoiceId) => {
    if (!useCredit || !creditNoteId) return { applied: false };
    const amt = parseFloat(creditAmount);
    if (!(amt > 0)) return { applied: false };
    try {
      const res = await fetch(`${API_URL}/invoices/${invoiceId}/apply-credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credit_note_id: creditNoteId, amount: amt })
      });
      const data = await res.json();
      if (!data.success) return { applied: false, error: data.error || 'Failed to allocate credit' };
      return { applied: true, message: data.message };
    } catch (error) {
      console.error('Error allocating credit:', error);
      return { applied: false, error: 'Failed to allocate credit' };
    }
  };

  // ============================================
  // ISSUE INVOICE (proforma -> tax invoice)
  // ============================================
  const confirmIssue = async () => {
    if (!issueTarget) return;
    setIssuing(true);
    try {
      const response = await fetch(`${API_URL}/invoices/${issueTarget.id}/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_terms: issueTerms })
      });
      const data = await response.json();
      if (data.success) {
        const creditRes = await applyCreditIfNeeded(issueTarget.id);
        if (creditRes.applied) {
          toast.success(`✅ Invoice ${data.invoice?.invoice_number} issued · ${creditRes.message}`);
        } else if (creditRes.error) {
          // The invoice IS issued — but the credit did NOT allocate. Make this
          // impossible to miss; retry from Record Payment on the invoice.
          toast.warn(
            `Invoice ${data.invoice?.invoice_number} was issued, but the client credit was NOT allocated (${creditRes.error}). Open Record Payment on that invoice and allocate the credit there.`,
            { autoClose: 15000 }
          );
        } else {
          toast.success(`✅ Invoice ${data.invoice?.invoice_number} issued`);
        }
        setIssueTarget(null);
        fetchInvoices();
      } else {
        toast.error(data.error || 'Failed to issue invoice');
      }
    } catch (error) {
      console.error('Error issuing invoice:', error);
      toast.error('Failed to issue invoice');
    } finally {
      setIssuing(false);
    }
  };

  // ============================================
  // RECORD PAYMENT
  // ============================================
  const openPayModal = (invoice) => {
    setPayTarget(invoice);
    // invoiceBalance returns R0 for a voided/cancelled/credited invoice — there is nothing to receive.
    const balance = invoiceBalance(invoice);
    setPayForm({
      amount: balance > 0 ? balance.toFixed(2) : '',
      payment_date: new Date().toISOString().split('T')[0],
      method: 'Bank Transfer',
      reference: '',
      notes: '',
      pop_file: null
    });
    // Offer any client credit against this invoice too.
    loadAvailableCredit(invoice);
  };

  const handlePayChange = (field, value) => {
    setPayForm(prev => ({ ...prev, [field]: value }));
  };

  const confirmPayment = async () => {
    if (!payTarget) return;
    const payAmount = parseFloat(payForm.amount) || 0;
    const creditAmt = useCredit ? (parseFloat(creditAmount) || 0) : 0;
    if (payAmount <= 0 && creditAmt <= 0) {
      toast.error('Enter a payment amount, or allocate the client credit');
      return;
    }
    if (payAmount > 0 && !payForm.payment_date) {
      toast.error('Select a payment date');
      return;
    }
    setSavingPay(true);
    try {
      const parts = [];

      // 1. Allocate client credit first (if the admin opted in)
      if (creditAmt > 0) {
        const creditRes = await applyCreditIfNeeded(payTarget.id);
        if (!creditRes.applied) {
          toast.error(creditRes.error || 'Failed to allocate credit');
          return;
        }
        parts.push(creditRes.message);
      }

      // 2. Record the receipt (skipped when the credit settles it in full)
      if (payAmount > 0) {
        let popUrl = null;
        if (payForm.pop_file) {
          const fd = new FormData();
          fd.append('image', payForm.pop_file);
          const ur = await fetch(`${API_URL}/uploads`, { method: 'POST', body: fd });
          const ud = await ur.json();
          if (ud.success) popUrl = ud.url || ud.file_url;
          else toast.error('Failed to upload proof of payment');
        }

        const response = await fetch(`${API_URL}/invoices/${payTarget.id}/payments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: payAmount,
            payment_date: payForm.payment_date,
            method: payForm.method,
            pop_url: popUrl,
            reference: payForm.reference || null,
            notes: payForm.notes || null
          })
        });
        const data = await response.json();
        if (!data.success) {
          toast.error(data.error || 'Failed to record payment');
          return;
        }
        parts.push(data.message || 'Payment recorded');
      }

      toast.success(parts.join(' · '));
      setPayTarget(null);
      fetchInvoices();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    } finally {
      setSavingPay(false);
    }
  };


  // ============================================
  // CREDIT INVOICE (full credit + rewind job chain)
  // ============================================
  const openCreditNotes = () => {
    setShowCreditNotes(true);
    setLoadingCreditNotes(true);
    fetch(`${API_URL}/credit-notes`)
      .then((r) => r.json())
      .then((data) => { if (data.success) setCreditNotes(data.credit_notes || []); })
      .catch((err) => console.error('Error fetching credit notes:', err))
      .finally(() => setLoadingCreditNotes(false));
  };

  const openCreditModal = (invoice) => {
    setCreditTarget(invoice);
    setCreditReason('');
  };

  const confirmCredit = async () => {
    if (!creditTarget) return;
    if (!creditReason || !creditReason.trim()) {
      toast.error('Enter a reason for the credit');
      return;
    }
    setCrediting(true);
    try {
      const response = await fetch(`${API_URL}/invoices/${creditTarget.id}/credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: creditReason.trim() })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Invoice credited');
        setCreditTarget(null);
        setCreditReason('');
        fetchInvoices();
      } else {
        toast.error(data.error || 'Failed to credit invoice');
      }
    } catch (error) {
      console.error('Error crediting invoice:', error);
      toast.error('Failed to credit invoice');
    } finally {
      setCrediting(false);
    }
  };


  // ============================================
  // STATEMENT
  // ============================================
  const openStatement = async (invoice) => {
    setStatementTarget(invoice);
    setStatement(null);
    setLoadingStatement(true);
    try {
      const response = await fetch(`${API_URL}/invoices/${invoice.id}/statement`);
      const data = await response.json();
      if (data.success) {
        setStatement(data);
      }
    } catch (error) {
      console.error('Error fetching statement:', error);
    } finally {
      setLoadingStatement(false);
    }
  };

  // ============================================
  // PO UPLOAD / VIEW
  // ============================================
  const openPoModal = (invoice) => {
    setPoTarget(invoice);
    const poRef = invoice?.job?.loadcon_ref_number || invoice?.jobs?.loadcon_ref_number || '';
    setPoForm({ ref_number: poRef, po_file: null });
  };

  const confirmPoUpload = async () => {
    if (!poTarget) return;
    const jobId = poTarget.job_id;
    if (!jobId) {
      toast.error('This invoice is not linked to a job');
      return;
    }
    if (!poForm.po_file && !poForm.ref_number) {
      toast.error('Attach a file or enter a PO number');
      return;
    }
    setUploadingPo(true);
    try {
      let poUrl = null;
      if (poForm.po_file) {
        const fd = new FormData();
        fd.append('image', poForm.po_file);
        const ur = await fetch(`${API_URL}/uploads`, { method: 'POST', body: fd });
        const ud = await ur.json();
        if (ud.success) poUrl = ud.url || ud.file_url;
        else toast.error('Failed to upload PO file');
      }

      const response = await fetch(`${API_URL}/jobs/${jobId}/po`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loadcon_file_url: poUrl,
          loadcon_ref_number: poForm.ref_number || null
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'PO attached to job');
        setPoTarget(null);
        fetchInvoices();
      } else {
        toast.error(data.error || 'Failed to attach PO');
      }
    } catch (error) {
      console.error('Error uploading PO:', error);
      toast.error('Failed to upload PO');
    } finally {
      setUploadingPo(false);
    }
  };

  // ============================================
  // DETAIL VIEW
  // ============================================
  const openDetail = async (invoice) => {
    setViewTarget(invoice);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const response = await fetch(`${API_URL}/invoices/${invoice.id}`);
      const data = await response.json();
      if (data.success) {
        setDetail(data);
      }
      fetchSubbieCosts(invoice.id);
    } catch (error) {
      console.error('Error fetching invoice detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };


  // ============================================
  // MISC HANDLERS
  // ============================================
  const handleUpdateStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/invoices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.success) {
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null); // invoice id to delete

  const handleDelete = (id) => {
    setDeleteTarget(id);
  };

  const performDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await fetch(`${API_URL}/invoices/${deleteTarget}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
    }
    setDeleteTarget(null);
  };

  const addItem = () => {
    setInvoiceItems([...invoiceItems, { description: '', amount: '', cost_price: '' }]);
  };

  const removeItem = (index) => {
    const newItems = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(newItems);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...invoiceItems];
    newItems[index][field] = value;
    setInvoiceItems(newItems);
  };

  const getFilteredInvoices = () => {
    let filtered = invoices;
    if (filter !== 'all') {
      filtered = filtered.filter(i => i.status === filter);
    }
    if (searchTerm) {
      filtered = filtered.filter(i =>
        i.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.client?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.jobs?.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.proforma_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'sent': 'bg-blue-100 text-blue-600',
      'paid': 'bg-green-100 text-green-600',
      'overdue': 'bg-red-100 text-red-600',
      'cancelled': 'bg-gray-100 text-gray-600',
      'proforma': 'bg-purple-100 text-purple-600',
      'tax_invoice': 'bg-indigo-100 text-indigo-600',
      'partially_paid': 'bg-orange-100 text-orange-600',
      'credited': 'bg-red-100 text-red-700',
      'partially_credited': 'bg-orange-100 text-red-600',
      // Voided = cancelled before any payment was received. Deliberately distinct from "Credited",
      // which means money came in and is owed back to the client.
      'void': 'bg-gray-200 text-gray-700'
    };
    return `px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.pending}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending',
      'sent': 'Sent',
      'paid': 'Paid',
      'overdue': 'Overdue',
      'cancelled': 'Cancelled',
      'proforma': 'Proforma',
      'tax_invoice': 'Tax Invoice',
      'partially_paid': 'Partially Paid',
      'credited': 'Credited',
      'partially_credited': 'Partially Credited',
      'void': 'Voided'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': <FaClock size={14} />,
      'sent': <FaEnvelope size={14} />,
      'paid': <FaCheck size={14} />,
      'overdue': <FaTimes size={14} />,
      'cancelled': <FaTimes size={14} />,
      'proforma': <FaFileInvoice size={14} />,
      'tax_invoice': <FaFileInvoice size={14} />,
      'partially_paid': <FaMoneyBillWave size={14} />,
      'credited': <FaUndo size={14} />,
      'partially_credited': <FaUndo size={14} />,
      'void': <FaUndo size={14} />
    };
    return icons[status] || <FaClock size={14} />;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'R0.00';
    return `R${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getPoUrl = (invoice) => {
    return invoice?.job?.loadcon_file_url || invoice?.jobs?.loadcon_file_url || null;
  };

  const filteredInvoices = getFilteredInvoices();

  // ── Client-credit prompt maths (issue + payment modals) ──
  // Max we may allocate = the client's available credit, capped by what the
  // invoice still owes.
  const creditMax = Math.min(
    Number(creditInfo?.total || 0),
    Number(creditTotals?.balance || 0)
  );
  const creditRefs = (creditInfo?.notes || []).map((n) => n.number).filter(Boolean).join(', ');
  const creditPending = useCredit ? (parseFloat(creditAmount) || 0) : 0;
  const balanceAfterCredit = Math.max(Number(creditTotals?.balance || 0) - creditPending, 0);


  return (
    <AdminLayout activeTab="invoices" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">📄 Invoices</h1>
          <p className="text-gray-500 text-sm">Manage all invoices</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ReceivePaymentButton onDone={fetchInvoices} />
          {(user.role === 'super_admin' || (user.permissions || []).includes('create_invoices')) && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
            >
              <FaPlus size={14} /> Create Invoice
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm text-gray-600">Filter:</span>
          </div>
          <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-sm ${filter === 'all' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            All ({invoices.length})
          </button>
          <button onClick={() => setFilter('proforma')} className={`px-3 py-1 rounded-full text-sm ${filter === 'proforma' ? 'bg-navy text-white' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'}`}>
            Proforma
          </button>
          <button onClick={() => setFilter('tax_invoice')} className={`px-3 py-1 rounded-full text-sm ${filter === 'tax_invoice' ? 'bg-navy text-white' : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'}`}>
            Tax Invoice
          </button>
          <button onClick={() => setFilter('sent')} className={`px-3 py-1 rounded-full text-sm ${filter === 'sent' ? 'bg-navy text-white' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}>
            Sent
          </button>
          <button onClick={() => setFilter('pending')} className={`px-3 py-1 rounded-full text-sm ${filter === 'pending' ? 'bg-navy text-white' : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'}`}>
            Pending
          </button>
          <button onClick={() => setFilter('partially_paid')} className={`px-3 py-1 rounded-full text-sm ${filter === 'partially_paid' ? 'bg-navy text-white' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}>
            Partially Paid
          </button>
          <button onClick={() => setFilter('paid')} className={`px-3 py-1 rounded-full text-sm ${filter === 'paid' ? 'bg-navy text-white' : 'bg-green-100 text-green-600 hover:bg-green-200'}`}>
            Paid
          </button>
          <button onClick={() => setFilter('overdue')} className={`px-3 py-1 rounded-full text-sm ${filter === 'overdue' ? 'bg-navy text-white' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}>
            Overdue
          </button>
          <button onClick={() => setFilter('credited')} className={`px-3 py-1 rounded-full text-sm ${filter === 'credited' ? 'bg-navy text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>
            Credited
          </button>
          <button onClick={() => setFilter('partially_credited')} className={`px-3 py-1 rounded-full text-sm ${filter === 'partially_credited' ? 'bg-navy text-white' : 'bg-orange-100 text-red-600 hover:bg-orange-200'}`}>
            Partially Credited
          </button>
          <button onClick={() => setFilter('void')} className={`px-3 py-1 rounded-full text-sm ${filter === 'void' ? 'bg-navy text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
            Voided
          </button>
          <button onClick={openCreditNotes} className="px-3 py-1 rounded-full text-sm bg-gray-800 text-white hover:bg-gray-700">
            🧾 Credit Notes
          </button>
          <div className="flex-1"></div>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green w-64"
            />
          </div>
        </div>
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">📄</div>
            <p className="text-gray-500">Loading invoices...</p>
          </div>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-6xl mb-4">📄</div>
            <h3 className="text-xl font-semibold text-navy mb-2">No Invoices</h3>
            <p className="text-gray-500">Create your first invoice by clicking the button above.</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice #</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Job</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Due</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>

                {filteredInvoices.map((invoice) => {
                  const poUrl = getPoUrl(invoice);
                  const creditApplied = parseFloat(invoice.credit_applied || 0);
                  // A voided/cancelled/credited invoice is not collectable, so its balance is R0.
                  // (It used to read "total minus nothing" and keep showing R6,325 after a void.)
                  const balance = invoiceBalance(invoice);
                  const canIssue = invoice.status === 'proforma';
                  const canPay = ['sent', 'pending', 'tax_invoice', 'partially_paid', 'overdue'].includes(invoice.status);
                  const canCredit = ['sent', 'pending', 'tax_invoice', 'partially_paid', 'paid', 'overdue'].includes(invoice.status);
                  const isProforma = invoice.status === 'proforma';
                  const canDownloadInvoice = !isProforma && invoice.status !== 'cancelled';
                  return (
                    <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-navy">
                        {invoice.invoice_number || invoice.proforma_number}
                        {invoice.invoice_type === 'supplementary' && (
                          <span className="ml-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Supplementary</span>
                        )}
                        {invoice.proforma_number && invoice.status !== 'proforma' && (
                          <div className="text-xs text-gray-400">PF: {invoice.proforma_number}</div>
                        )}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600">{invoice.client?.company_name || '-'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{invoice.jobs?.job_number || '-'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{formatDate(invoice.invoice_date)}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{formatDate(invoice.due_date)}</td>
                      <td className="px-6 py-3 text-sm font-medium text-navy">{formatCurrency(invoice.total_amount)}</td>
                      <td className={`px-6 py-3 text-sm font-medium ${balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        {formatCurrency(balance)}
                        {creditApplied > 0 && (
                          <div className="text-[10px] font-normal text-green-600">less credit {formatCurrency(creditApplied)}</div>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <span className={getStatusBadge(invoice.status)}>
                          {getStatusIcon(invoice.status)} {getStatusLabel(invoice.status)}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openDetail(invoice)} className="text-gray-400 hover:text-blue-600 transition-colors" title="View">
                            <FaEye size={16} />
                          </button>
                          {isProforma && (
                            <ProformaPDFButton invoice={invoice} iconOnly />
                          )}
                          {canDownloadInvoice && (
                            <ClientInvoicePDFButton
                              invoiceId={invoice.id}
                              invoice={invoice}
                              iconOnly
                            />
                          )}
                          {canIssue && (
                            <button onClick={() => { setIssueTarget(invoice); setIssueTerms(invoice.payment_terms || '30 Days'); loadAvailableCredit(invoice); }} className="text-gray-400 hover:text-green-600 transition-colors" title="Issue Invoice">
                              <FaCheck size={16} />
                            </button>
                          )}
                          {canPay && (
                            <button onClick={() => openPayModal(invoice)} className="text-gray-400 hover:text-green-600 transition-colors" title="Record Payment">
                              <FaMoneyBillWave size={16} />
                            </button>
                          )}
                          {canCredit && (
                            <button onClick={() => openCreditModal(invoice)} className="text-gray-400 hover:text-red-600 transition-colors" title={String(invoice.invoice_type || 'standard') === 'supplementary'
                              ? (Number(invoice.paid_amount || 0) <= 0.009 ? 'Void Supplementary (original invoice & job kept)' : 'Credit Supplementary (original invoice & job kept)')
                              : (Number(invoice.paid_amount || 0) <= 0.009 ? 'Void Invoice (no payment received)' : 'Credit Invoice (rewind job)')}>
                              <FaUndo size={16} />
                            </button>
                          )}
                          <button onClick={() => openStatement(invoice)} className="text-gray-400 hover:text-blue-600 transition-colors" title="Statement">
                            <FaListUl size={16} />
                          </button>
                          <button onClick={() => openPoModal(invoice)} className={`${poUrl ? 'text-green-600' : 'text-gray-400'} hover:text-green-600 transition-colors`} title={poUrl ? 'View / Upload PO' : 'Upload PO'}>
                            <FaPaperclip size={16} />
                          </button>
                          <button onClick={() => handleDelete(invoice.id)} className="text-gray-400 hover:text-red-600 transition-colors" title="Delete">
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 pb-0">
              <h2 className="text-xl font-bold text-navy">Create Invoice</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateInvoice} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job (optional) <span className="text-gray-400 font-normal">— links the invoice &amp; auto-fills the client</span>
                </label>
                <select
                  value={selectedJob}
                  onChange={(e) => handleJobChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                >
                  <option value="">No job — manual invoice</option>
                  {jobs.filter(j => j.status !== 'cancelled').map(j => {
                    const hasInv = jobInvoiceMap[j.id];
                    return (
                      <option key={j.id} value={j.id}>
                        {j.job_number} — {j.commodity || j.origin || ''} ({j.status}){hasInv ? ` — has ${hasInv.num}` : ''}
                      </option>
                    );
                  })}
                </select>
                {selectedJob && jobInvoiceMap[selectedJob] && (
                  <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <input
                      id="supplementary-charge"
                      type="checkbox"
                      checked={isSupplementary}
                      onChange={(e) => setIsSupplementary(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <label htmlFor="supplementary-charge" className="text-xs text-amber-800">
                      <span className="font-semibold">Supplementary charge</span> — this is an EXTRA charge on top of{' '}
                      {jobInvoiceMap[selectedJob].num}. The original invoice stays untouched.
                    </label>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select
                  value={selectedClient}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  required
                >
                  <option value="">Select client...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                >
                  {PAYMENT_TERM_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">{getDueHint()}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Line Items</label>
                <div className="space-y-2">
                  {invoiceItems.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                      <input
                        type="number"
                        placeholder="Cost"
                        value={item.cost_price}
                        onChange={(e) => updateItem(index, 'cost_price', e.target.value)}
                        className="w-24 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                      <input
                        type="number"
                        placeholder="Amount"
                        value={item.amount}
                        onChange={(e) => updateItem(index, 'amount', e.target.value)}
                        className="w-32 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                      {invoiceItems.length > 1 && (
                        <button type="button" onClick={() => removeItem(index)} className="text-red-500 hover:text-red-700">
                          <FaTrash size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addItem} className="mt-2 text-sm text-blue-600 hover:text-blue-800">
                  <FaPlus className="inline mr-1" size={12} /> Add Item
                </button>
              </div>

              {/* VAT toggle + live totals (mirrors the quote page) */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4">
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={vatIncluded}
                    onChange={(e) => setVatIncluded(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Include VAT (15%)</span>
                  <span className="text-xs text-gray-400">— untick for VAT-exempt / cross-border lines</span>
                </label>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-navy">R{formSubtotal.toFixed(2)}</span>
                  </div>
                  {vatIncluded && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">VAT (15%)</span>
                      <span className="font-medium text-navy">R{formVat.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-2 flex justify-between">
                    <span className="text-base font-bold text-navy">Total</span>
                    <span className="text-base font-bold text-green-600">R{formTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors">
                  <FaPlus className="inline mr-2" /> Create Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Issue Invoice Modal */}
      {issueTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIssueTarget(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-2">Issue Tax Invoice</h2>
            <p className="text-sm text-gray-500 mb-4">
              Convert proforma <strong>{issueTarget.proforma_number || issueTarget.invoice_number}</strong> into the tax invoice.
              A clean <strong>INV-00001</strong> number will be assigned.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
              <select value={issueTerms} onChange={(e) => setIssueTerms(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="30 Days">30 Days</option>
                <option value="14 Days">14 Days</option>
                <option value="7 Days">7 Days</option>
                <option value="Due on Receipt">Due on Receipt</option>
                <option value="COD">COD</option>
              </select>
            </div>

            {/* Client credit available to allocate against this invoice */}
            {loadingCredit && (
              <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
                <FaSpinner className="animate-spin" size={12} /> Checking client credit…
              </div>
            )}
            {!loadingCredit && Number(creditInfo?.total) > 0 && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-sm font-medium text-green-900">
                  💳 This client has {formatCurrency(creditInfo.total)} credit available to allocate
                </p>
                <p className="mt-0.5 text-[11px] text-green-700">Unallocated credit on this client's account — tick below to apply it to this invoice.</p>
                {creditRefs && (
                  <p className="mt-0.5 text-xs text-green-700">Credit note{creditInfo.notes.length !== 1 ? 's' : ''}: {creditRefs}</p>
                )}
                {creditMax > 0 ? (
                  <>
                    <label className="mt-2 flex items-center gap-2 text-sm text-green-900">
                      <input
                        type="checkbox"
                        checked={useCredit}
                        onChange={(e) => {
                          setUseCredit(e.target.checked);
                          setCreditAmount(String(creditMax.toFixed(2)));
                        }}
                      />
                      Allocate credit against this invoice
                    </label>
                    {useCredit && (
                      <div className="mt-2 space-y-2">
                        {creditInfo.notes.length > 1 && (
                          <select
                            value={creditNoteId}
                            onChange={(e) => setCreditNoteId(e.target.value)}
                            className="w-full rounded-lg border border-green-200 bg-white px-3 py-2 text-sm"
                          >
                            {creditInfo.notes.map((n) => (
                              <option key={n.id} value={n.id}>{n.number} — {formatCurrency(n.available)} available</option>
                            ))}
                          </select>
                        )}
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={creditMax}
                            value={creditAmount}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const num = parseFloat(raw);
                              setCreditAmount(raw !== '' && num > creditMax ? String(creditMax.toFixed(2)) : raw);
                            }}
                            className="w-40 rounded-lg border border-green-200 bg-white px-3 py-2 text-sm"
                          />
                          <span className="text-xs text-green-700">Max {formatCurrency(creditMax)}</span>
                        </div>
                        <p className="text-xs text-green-800">
                          Balance after credit: <strong>{formatCurrency(balanceAfterCredit)}</strong>
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="mt-1 text-xs text-green-700">Nothing left on this invoice to allocate against.</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={() => setIssueTarget(null)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={confirmIssue} disabled={issuing} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                {issuing ? <FaSpinner className="animate-spin" size={14} /> : <FaCheck size={14} />}
                {issuing ? 'Issuing...' : 'Issue Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Record Payment Modal */}
      {payTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setPayTarget(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-2">Record Payment</h2>
            <p className="text-sm text-gray-500 mb-3">
              Invoice <strong>{payTarget.invoice_number || payTarget.proforma_number}</strong> — {payTarget.client?.company_name || ''}
            </p>

            {/* Money breakdown: total → credit → payments → balance */}
            <div className="mb-4 space-y-1 rounded-lg bg-gray-50 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span className="font-medium text-navy">{formatCurrency(creditTotals?.total ?? payTarget.total_amount)}</span>
              </div>
              {Number(creditTotals?.credit_applied ?? payTarget.credit_applied) > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Less credit applied</span>
                  <span>-{formatCurrency(creditTotals?.credit_applied ?? payTarget.credit_applied)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Payments received</span>
                <span className="font-medium">{formatCurrency(creditTotals?.paid ?? payTarget.paid_amount)}</span>
              </div>
              {creditPending > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Credit to allocate now</span>
                  <span>-{formatCurrency(creditPending)}</span>
                </div>
              )}
              <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 font-semibold">
                <span className="text-navy">Balance</span>
                <span className={balanceAfterCredit > 0 ? 'text-orange-600' : 'text-green-600'}>{formatCurrency(balanceAfterCredit)}</span>
              </div>
            </div>

            {/* Client credit available to allocate against this invoice */}
            {loadingCredit && (
              <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
                <FaSpinner className="animate-spin" size={12} /> Checking client credit…
              </div>
            )}
            {!loadingCredit && Number(creditInfo?.total) > 0 && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-sm font-medium text-green-900">
                  💳 This client has {formatCurrency(creditInfo.total)} credit available to allocate
                </p>
                <p className="mt-0.5 text-[11px] text-green-700">Unallocated credit on this client's account — tick below to apply it to this invoice.</p>
                {creditRefs && (
                  <p className="mt-0.5 text-xs text-green-700">Credit note{creditInfo.notes.length !== 1 ? 's' : ''}: {creditRefs}</p>
                )}
                {creditMax > 0 ? (
                  <>
                    <label className="mt-2 flex items-center gap-2 text-sm text-green-900">
                      <input
                        type="checkbox"
                        checked={useCredit}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setUseCredit(on);
                          setCreditAmount(String(creditMax.toFixed(2)));
                          // Leave the receipt BLANK unless cash actually arrived —
                          // ticking the credit box must never record a payment.
                          if (on) setPayForm((prev) => ({ ...prev, amount: '' }));
                        }}
                      />
                      Allocate credit against this invoice
                    </label>
                    {useCredit && (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={creditMax}
                          value={creditAmount}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const num = parseFloat(raw);
                            setCreditAmount(raw !== '' && num > creditMax ? String(creditMax.toFixed(2)) : raw);
                          }}
                          className="w-40 rounded-lg border border-green-200 bg-white px-3 py-2 text-sm"
                        />
                        <span className="text-xs text-green-700">Max {formatCurrency(creditMax)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="mt-1 text-xs text-green-700">Nothing left on this invoice to allocate against.</p>
                )}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount received</label>
                <input type="number" step="0.01" min="0.01" value={payForm.amount} onChange={(e) => handlePayChange('amount', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                <p className="mt-1 text-[11px] text-gray-400">Cash received now. Leave blank if the credit note settles it in full.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                <input type="date" value={payForm.payment_date} onChange={(e) => handlePayChange('payment_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                <select value={payForm.method} onChange={(e) => handlePayChange('method', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option>Bank Transfer</option>
                  <option>EFT</option>
                  <option>Cheque</option>
                  <option>Cash</option>
                  <option>Card</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input type="text" placeholder="e.g. EFT ref / deposit ref" value={payForm.reference} onChange={(e) => handlePayChange('reference', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proof of Payment</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => handlePayChange('pop_file', e.target.files[0])} className="w-full text-sm" />
                {payForm.pop_file && <p className="text-xs text-green-600 mt-1">✅ {payForm.pop_file.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input type="text" value={payForm.notes} onChange={(e) => handlePayChange('notes', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setPayTarget(null)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={confirmPayment} disabled={savingPay} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                {savingPay ? <FaSpinner className="animate-spin" size={14} /> : <FaMoneyBillWave size={14} />}
                {savingPay ? 'Saving...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Credit Notes list Modal */}
      {showCreditNotes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreditNotes(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy">Credit Notes</h2>
              <button onClick={() => setShowCreditNotes(false)} className="text-gray-500 hover:text-navy"><FaTimes size={20} /></button>
            </div>
            {loadingCreditNotes ? (
              <div className="flex justify-center py-8"><FaSpinner className="animate-spin text-2xl text-green-500" /></div>
            ) : creditNotes.length === 0 ? (
              <p className="text-sm text-gray-500">No credit notes yet. Use the Credit action on a paid / sent invoice to create one.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                    <th className="px-3 py-2">Number</th>
                    <th className="px-3 py-2">Client</th>
                    <th className="px-3 py-2">Invoice</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {creditNotes.map((cn) => (
                    <tr key={cn.id} className="border-b border-gray-100">
                      <td className="px-3 py-2 font-medium text-navy">{cn.credit_note_number}</td>
                      <td className="px-3 py-2">{cn.client?.company_name || '-'}</td>
                      <td className="px-3 py-2">{cn.invoice?.invoice_number || '-'}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(cn.total_amount)}</td>
                      <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 capitalize">{cn.status.replace(/_/g, ' ')}</span></td>
                      <td className="px-3 py-2 text-right"><CreditNotePDFButton creditNoteId={cn.id} iconOnly /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}


      {/* Credit Invoice Modal */}
      {creditTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setCreditTarget(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {/* Nothing paid -> this is a VOID, not a credit. Calling it a credit would be a lie:
                there is no money to give back. Saying so here stops the office expecting a credit. */}
            {Number(creditTarget.paid_amount || 0) <= 0.009 ? (
              <>
                <h2 className="text-xl font-bold text-gray-700 mb-2">
                  {String(creditTarget.invoice_type || 'standard') === 'supplementary' ? 'Void Supplementary Invoice' : 'Void Invoice'}
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>{creditTarget.invoice_number || creditTarget.proforma_number}</strong> for{' '}
                  <strong>{formatCurrency(creditTarget.total_amount)}</strong> has received{' '}
                  <strong>no payment</strong>, so there is nothing to credit — this will{' '}
                  <strong>void</strong> the invoice.{' '}
                  {String(creditTarget.invoice_type || 'standard') === 'supplementary' ? (
                    <>It was raised as an <strong>extra charge</strong> on an existing invoice, so the
                    original invoice, the linked job, its loads and the source quote are all left
                    <strong> untouched</strong>.</>
                  ) : (
                    <>It also <strong>rewinds the job chain</strong>: the linked job, loads and subbie
                    payable are cancelled and the source quote is marked void.</>
                  )}
                  No credit note is created and no credit is left standing on the client.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-red-600 mb-2">
                  {String(creditTarget.invoice_type || 'standard') === 'supplementary' ? 'Credit Supplementary Invoice' : 'Credit Invoice'}
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Credit <strong>{creditTarget.invoice_number || creditTarget.proforma_number}</strong> for{' '}
                  <strong>{formatCurrency(creditTarget.total_amount)}</strong>.
                  {String(creditTarget.invoice_type || 'standard') === 'supplementary' ? (
                    <>It was raised as an <strong>extra charge</strong> on an existing invoice, so this credits
                    only the extra charge — the original invoice, the linked job and the source quote are left
                    <strong> untouched</strong>.</>
                  ) : (
                    <>This creates a credit note and <strong>rewinds the job chain</strong> — it cancels the linked
                    job, loads and subbie payable, and credits the source quote. The credit then sits on the
                    client to be applied to the re-quoted work.</>
                  )}
                </p>
              </>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for credit *</label>
              <textarea
                rows={3}
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder="e.g. Wrong crane on site (client fault) — requoting for 2 cranes"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCreditTarget(null)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={confirmCredit} disabled={crediting} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                {crediting ? <FaSpinner className="animate-spin" size={14} /> : <FaUndo size={14} />}
                {crediting
                  ? 'Working...'
                  : (Number(creditTarget.paid_amount || 0) <= 0.009 ? 'Void Invoice' : 'Create Credit Note')}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Statement Modal */}
      {statementTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setStatementTarget(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy">Statement — {statement?.invoice?.invoice_number || statementTarget.invoice_number || statementTarget.proforma_number}</h2>
              <button onClick={() => setStatementTarget(null)} className="text-gray-500 hover:text-navy"><FaTimes size={20} /></button>
            </div>
            {loadingStatement ? (
              <div className="flex justify-center py-8"><FaSpinner className="animate-spin text-2xl text-green-500" /></div>
            ) : statement ? (
              <div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Total</p><p className="font-bold text-navy">{formatCurrency(statement.totals.total)}</p></div>
                  <div className="p-3 bg-green-50 rounded-lg"><p className="text-xs text-green-600">Credit + Paid</p><p className="font-bold text-green-700">{formatCurrency(parseFloat(statement.totals.credit_applied || 0) + parseFloat(statement.totals.paid || 0))}</p></div>
                  <div className="p-3 bg-orange-50 rounded-lg"><p className="text-xs text-orange-600">Balance</p><p className="font-bold text-orange-700">{formatCurrency(statement.totals.balance)}</p></div>
                </div>

                {/* Credits allocated against this invoice */}
                {(statement.allocations || []).length > 0 && (
                  <div className="mb-4">
                    <h3 className="mb-2 text-sm font-semibold text-navy">Credits applied</h3>
                    <div className="space-y-1">
                      {statement.allocations.map((a) => (
                        <div key={a.id} className="flex justify-between rounded-lg bg-green-50 px-3 py-2 text-sm">
                          <span className="text-green-800">
                            {a.credit_note?.credit_note_number || 'Credit note'}
                            {a.credit_note?.credit_date ? ` · ${formatDate(a.credit_note.credit_date)}` : ''}
                          </span>
                          <span className="font-medium text-green-700">-{formatCurrency(a.amount_applied)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <h3 className="text-sm font-semibold text-navy mb-2">Payment History</h3>
                {statement.payments.length === 0 ? (
                  <p className="text-sm text-gray-500">No payments recorded yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Date</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Method</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Ref</th>
                        <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.payments.map((p) => (
                        <tr key={p.id} className="border-b border-gray-100">
                          <td className="px-3 py-2">{formatDate(p.payment_date)}</td>
                          <td className="px-3 py-2">{p.method}</td>
                          <td className="px-3 py-2">{p.reference || '-'}</td>
                          <td className="px-3 py-2 text-right font-medium text-green-700">{formatCurrency(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Failed to load statement.</p>
            )}
          </div>
        </div>
      )}


      {/* PO Upload Modal */}
      {poTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setPoTarget(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-2">Purchase Order</h2>
            <p className="text-sm text-gray-500 mb-4">
              Job <strong>{poTarget.jobs?.job_number || 'N/A'}</strong> — {poTarget.jobs?.loadcon_ref_number ? `PO: ${poTarget.jobs.loadcon_ref_number}` : 'No PO attached yet'}
            </p>
            {getPoUrl(poTarget) && (
              <div className="mb-4 p-3 bg-green-50 rounded-lg">
                <a href={getPoUrl(poTarget)} target="_blank" rel="noopener noreferrer" className="text-sm text-green-700 hover:underline flex items-center gap-2">
                  <FaEye size={14} /> View attached PO
                </a>
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO / Loadcon Ref Number</label>
                <input type="text" placeholder="e.g. PO-2026-001" value={poForm.ref_number} onChange={(e) => setPoForm(prev => ({ ...prev, ref_number: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO Document</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setPoForm(prev => ({ ...prev, po_file: e.target.files[0] }))} className="w-full text-sm" />
                {poForm.po_file && <p className="text-xs text-green-600 mt-1">✅ {poForm.po_file.name}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setPoTarget(null)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={confirmPoUpload} disabled={uploadingPo} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                {uploadingPo ? <FaSpinner className="animate-spin" size={14} /> : <FaPaperclip size={14} />}
                {uploadingPo ? 'Uploading...' : 'Save PO'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Detail Modal */}
      {viewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setViewTarget(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy">
                {viewTarget.invoice_number || viewTarget.proforma_number || 'Invoice'}
              </h2>
              <button onClick={() => setViewTarget(null)} className="text-gray-500 hover:text-navy"><FaTimes size={20} /></button>
            </div>
            {loadingDetail ? (
              <div className="flex justify-center py-8"><FaSpinner className="animate-spin text-2xl text-green-500" /></div>
            ) : detail ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Status</p><p className="font-medium text-navy">{getStatusLabel(detail.invoice.status)}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Client</p><p className="font-medium text-navy">{detail.invoice.client?.company_name || '-'}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Job</p><p className="font-medium text-navy">{detail.invoice.jobs?.job_number || '-'}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Quote</p><p className="font-medium text-navy">{detail.invoice.quotes?.quote_number || '-'}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Proforma #</p><p className="font-medium text-navy">{detail.invoice.proforma_number || '-'}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Payment Terms</p><p className="font-medium text-navy">{detail.invoice.payment_terms || '30 Days'}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Invoice Date</p><p className="font-medium text-navy">{formatDate(detail.invoice.invoice_date)}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Due Date</p><p className="font-medium text-navy">{formatDate(detail.invoice.due_date)}</p></div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-navy mb-2">Line Items</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Description</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Qty</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Rate</th>
                        <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">Amount</th>
                        <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">Cost</th>
                        <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((item) => (
                        <tr key={item.id} className="border-b border-gray-100">
                          <td className="px-3 py-2">{item.description}</td>
                          <td className="px-3 py-2">{item.quantity_mt ?? '-'}</td>
                          <td className="px-3 py-2">{item.rate_per_mt != null ? formatCurrency(item.rate_per_mt) : '-'}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.amount)}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{item.cost_price != null ? formatCurrency(item.cost_price) : '-'}</td>
                          <td className="px-3 py-2 text-right">
                            {item.margin_amount != null && item.margin_amount !== 0
                              ? `${formatCurrency(item.margin_amount)} (${(parseFloat(item.margin_percentage) || 0).toFixed(1)}%)`
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex justify-end mt-2 space-x-4 text-sm">
                    <span className="text-gray-500">Subtotal: <strong className="text-navy">{formatCurrency(detail.invoice.subtotal)}</strong></span>
                    <span className="text-gray-500">VAT: <strong className="text-navy">{formatCurrency(detail.invoice.vat_amount)}</strong></span>
                    <span className="text-gray-500">Total: <strong className="text-navy">{formatCurrency(detail.invoice.total_amount)}</strong></span>
                    {parseFloat(detail.invoice.credit_applied || 0) > 0 && (
                      <span className="text-green-600">Credit: <strong>-{formatCurrency(detail.invoice.credit_applied)}</strong></span>
                    )}
                    <span className="text-gray-500">
                      Balance: <strong className={invoiceBalance(detail.invoice) > 0 ? 'text-orange-600' : 'text-green-600'}>
                        {formatCurrency(invoiceBalance(detail.invoice))}
                      </strong>
                    </span>
                  </div>

                  {/* Credits allocated against this invoice */}
                  {(detail.allocations || []).length > 0 && (
                    <div className="mt-3">
                      {detail.allocations.map((a) => (
                        <div key={a.id} className="flex justify-between rounded-lg bg-green-50 px-3 py-2 text-sm">
                          <span className="text-green-800">
                            💳 {a.credit_note?.credit_note_number || 'Credit note'} allocated
                          </span>
                          <span className="font-medium text-green-700">-{formatCurrency(a.amount_applied)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Subcontractor cost allocation */}
                <div className="mt-5 border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-navy">🧾 Subcontractor costs on this invoice</h3>
                    <span className="text-xs text-gray-400">Pays the subbie their share of this job</span>
                  </div>
                  {invoicePayables.length === 0 ? (
                    <p className="text-xs text-gray-500 mb-2">No subbie costs allocated yet.</p>
                  ) : (
                    <div className="space-y-1.5 mb-2">
                      {invoicePayables.map(p => (
                        <div key={p.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2 text-sm">
                          <span className="text-navy font-medium">{p.subcontractor?.company_name || 'Subcontractor'}</span>
                          <span className="text-gray-500">{p.invoice_number}</span>
                          <span className="font-medium text-navy">{formatCurrency(p.total_amount)}</span>
                          <span className="text-xs text-gray-500">{p.status === 'pending_payment' ? 'Pending' : p.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={allocForm.subcontractor_id}
                      onChange={(e) => setAllocForm(f => ({ ...f, subcontractor_id: e.target.value }))}
                      className="flex-1 min-w-[200px] px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      <option value="">Select subcontractor…</option>
                      {subbies.map(s => (
                        <option key={s.id} value={s.id}>{s.company_name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Cost (ex-VAT)"
                      value={allocForm.amount}
                      onChange={(e) => setAllocForm(f => ({ ...f, amount: e.target.value }))}
                      className="w-40 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <button
                      onClick={submitSubbieAlloc}
                      disabled={allocating}
                      className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
                    >
                      {allocating ? 'Allocating…' : 'Allocate'}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">Creates a pending payable for the subcontractor — it appears under <strong>Subbie Payments</strong> for payment.</p>
                </div>


                <div className="flex flex-wrap gap-2">
                  {viewTarget.status === 'proforma' && (
                    <button onClick={() => { setViewTarget(null); setIssueTarget(viewTarget); setIssueTerms(viewTarget.payment_terms || '30 Days'); loadAvailableCredit(viewTarget); }} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green flex items-center gap-2">
                      <FaCheck size={14} /> Issue Invoice
                    </button>
                  )}
                  {['sent', 'pending', 'tax_invoice', 'partially_paid', 'overdue'].includes(viewTarget.status) && (
                    <button onClick={() => { setViewTarget(null); openPayModal(viewTarget); }} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green flex items-center gap-2">
                      <FaMoneyBillWave size={14} /> Record Payment
                    </button>
                  )}
                  {['sent', 'pending', 'tax_invoice', 'partially_paid', 'paid', 'overdue'].includes(viewTarget.status) && (
                    <button onClick={() => { setViewTarget(null); openCreditModal(viewTarget); }} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-2">
                      <FaUndo size={14} /> Credit Invoice
                    </button>
                  )}
                  <button onClick={() => openStatement(viewTarget)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
                    <FaListUl size={14} /> Statement
                  </button>
                  <button onClick={() => { setViewTarget(null); openPoModal(viewTarget); }} className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 flex items-center gap-2">
                    <FaPaperclip size={14} /> PO
                  </button>
                  {viewTarget.status === 'proforma' ? (
                    <ProformaPDFButton invoice={viewTarget} />
                  ) : viewTarget.status !== 'cancelled' ? (
                    <ClientInvoicePDFButton invoiceId={viewTarget.id} invoice={viewTarget} />
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Failed to load invoice details.</p>
            )}
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Invoice"
        message="Delete this invoice?"
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
      />
    </AdminLayout>
  );
}

export default InvoicesPage;
